// src/context/EncomendasContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db, paths } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { getNormalizedDateString } from '../utils/helpers';

const EncomendasContext = createContext();

export const useEncomendasAuth = () => useContext(EncomendasContext);

export const EncomendasAuthProvider = ({ children }) => {
    const { userProfile } = useAuth();
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Extrair propriedades primitivas para evitar re-renderizações infinitas
    // caso o AuthContext retorne uma nova referência de objeto a cada render.
    const userCpf = userProfile?.cpf;
    const userNivel = userProfile?.nivel;

    useEffect(() => {
        let isMounted = true; // Previne vazamento de memória e atualizações após desmontagem

        const checkAuthorization = async () => {
            if (!isMounted) return;
            setIsLoading(true);
            
            if (!userCpf) {
                setIsAuthorized(false);
                setIsLoading(false);
                return;
            }
            
            // 1. Admin está SEMPRE autorizado a ver o link (para gerenciar)
            if (userNivel === 'admin') {
                 setIsAuthorized(true);
                 setIsLoading(false);
                 return;
            }

            // 2. Outros usuários (Cmt/Aux) precisam estar na escala
            try {
                // Obter data de HOJE (ex: 2025-11-12)
                const todayStr = getNormalizedDateString(new Date());
                
                // Obter data de ONTEM (ex: 2025-11-11)
                const yesterday = new Date(Date.now() - 86400000); // 24h em milissegundos
                const yesterdayStr = getNormalizedDateString(yesterday);
                
                const todayRef = doc(db, paths.escalasEncomendas, todayStr);
                const yesterdayRef = doc(db, paths.escalasEncomendas, yesterdayStr);
                
                const [todaySnap, yesterdaySnap] = await Promise.all([getDoc(todayRef), getDoc(yesterdayRef)]);
                
                if (!isMounted) return; // Cancela se o usuário saiu da tela durante a requisição

                let authorized = false;
                if (todaySnap.exists()) {
                    const data = todaySnap.data();
                    if (data.cpf_cg === userCpf || data.cpf_aux_cg === userCpf) authorized = true;
                }
                if (!authorized && yesterdaySnap.exists()) {
                    const data = yesterdaySnap.data();
                    if (data.cpf_cg === userCpf || data.cpf_aux_cg === userCpf) authorized = true;
                }
                
                setIsAuthorized(authorized);

            } catch (err) {
                console.error("Erro (EncomendasAuth):", err);
                if (isMounted) setIsAuthorized(false);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };
        
        checkAuthorization();

        // Cleanup function: roda quando o componente é destruído (ex: usuário mudou de tela)
        return () => {
            isMounted = false;
        };
    }, [userCpf, userNivel]); // Depende apenas das strings, e não do objeto inteiro

    return (
        <EncomendasContext.Provider value={{ isAuthorized, isLoading }}>
            {children}
        </EncomendasContext.Provider>
    );
};